// Chart Request Queue Manager
//
// Manages async chart loading with rate limiting and retry logic to prevent
// server overload (429 errors) on pages with many charts like classroom rosters.
//
// Features:
// - Request queuing with configurable concurrency
// - Exponential backoff for 429 responses
// - Request spacing to prevent server overload
// - Automatic retry with jitter
// - Global singleton to coordinate across all chart controllers

class ChartRequestQueue {
  constructor() {
    this.queue = []
    this.activeRequests = 0
    this.maxConcurrentRequests = 3 // Only 3 charts loading at once
    this.minRequestDelay = 100 // Minimum 100ms between requests
    this.lastRequestTime = 0
    this.processing = false
  }

  // Add a chart load request to the queue
  enqueue(requestFn, retryOptions = {}) {
    return new Promise((resolve, reject) => {
      const request = {
        requestFn,
        resolve,
        reject,
        retryCount: 0,
        maxRetries: retryOptions.maxRetries || 5,
        baseDelay: retryOptions.baseDelay || 1000,
        maxDelay: retryOptions.maxDelay || 10000
      }

      this.queue.push(request)
      this.processQueue()
    })
  }

  async processQueue() {
    // Prevent multiple processQueue calls from running simultaneously
    if (this.processing) {
      return
    }

    this.processing = true

    while (this.queue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const request = this.queue.shift()
      this.executeRequest(request)
    }

    this.processing = false
  }

  async executeRequest(request) {
    this.activeRequests++

    try {
      // Ensure minimum delay between requests
      const timeSinceLastRequest = Date.now() - this.lastRequestTime
      if (timeSinceLastRequest < this.minRequestDelay) {
        await this.delay(this.minRequestDelay - timeSinceLastRequest)
      }

      this.lastRequestTime = Date.now()

      // Execute the actual request
      const result = await request.requestFn()
      request.resolve(result)

    } catch (error) {
      // Handle 429 (Too Many Requests) with exponential backoff
      if (error.status === 429 && request.retryCount < request.maxRetries) {
        await this.retryWithBackoff(request, error)
      } else {
        request.reject(error)
      }
    } finally {
      this.activeRequests--
      // Process more requests if queue is not empty
      if (this.queue.length > 0) {
        // Small delay before processing next batch
        setTimeout(() => this.processQueue(), 50)
      }
    }
  }

  async retryWithBackoff(request, error) {
    request.retryCount++

    // Calculate exponential backoff delay with jitter
    const backoffDelay = Math.min(
      request.baseDelay * Math.pow(2, request.retryCount - 1),
      request.maxDelay
    )

    // Add random jitter (±25%) to prevent thundering herd
    const jitter = backoffDelay * 0.25 * (Math.random() * 2 - 1)
    const delayWithJitter = Math.max(100, backoffDelay + jitter)

    console.log(`Chart request failed with 429, retrying in ${Math.round(delayWithJitter)}ms (attempt ${request.retryCount}/${request.maxRetries})`)

    // Wait for backoff delay then re-queue the request
    await this.delay(delayWithJitter)
    this.queue.unshift(request) // Add back to front of queue for priority

    // Process queue after delay
    setTimeout(() => this.processQueue(), 10)
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // Get queue status for debugging
  getStatus() {
    return {
      queueLength: this.queue.length,
      activeRequests: this.activeRequests,
      processing: this.processing
    }
  }

  // Clear all pending requests (useful for cleanup)
  clear() {
    this.queue.forEach(request => {
      request.reject(new Error('Queue cleared'))
    })
    this.queue = []
  }
}

// Export singleton instance
export default new ChartRequestQueue()